/**
 * Wohin ein Tipp auf eine Benachrichtigung führen soll.
 *
 * **Der Server sagt es schon.** In jeder Push-Nutzlast steht eine Adresse
 * (`Notifier::payload()`): der Reise-Chat, ein Antwortverlauf, die Infoseite.
 * Der Service Worker öffnet sie beim Antippen — und das reicht, solange die
 * App geschlossen war.
 *
 * **Ist sie schon offen, reicht es auf dem iPhone nicht.** Safari kennt
 * `WindowClient.navigate` nicht; der Service Worker holt das Fenster dann nur
 * nach vorn, und es bleibt stehen, wo es war. Deshalb schickt er den Pfad
 * zusätzlich als Nachricht, und hier wird sie aufgefangen.
 *
 * **Keine zweite Wahrheit in der Nutzlast.** Es wäre verlockend, `trip_id` und
 * `with_user` mitzuschicken und den Weg hier zusammenzubauen — dann stünde die
 * Zuordnung „Antwort gehört in den Einzelverlauf" zweimal da, einmal im
 * Notifier und einmal hier, und die nächste Route trüge nur eine der beiden
 * nach. Der Client bekommt einen Pfad und springt dorthin, mehr weiß er nicht.
 */

/** Genau die Kürzung, die auch `api/client.ts` und `lib/push.ts` benutzen. */
const BASE = import.meta.env.BASE_URL.replace(/\/$/, '')

type Empfaenger = (pfad: string) => void

let empfaenger: Empfaenger | null = null

/**
 * Ein Ziel, das ankam, bevor jemand zuhörte.
 *
 * Beim Kaltstart wird das Fenster geöffnet *und* bekommt die Nachricht, lange
 * bevor React steht. Ohne diesen Zettel wäre sie weg, und der Tipp führte auf
 * die Startseite — also genau dorthin, wo man nicht hinwollte.
 *
 * Verbraucht heißt gelöscht: ein späteres Neuaufbauen der Oberfläche soll
 * nicht ein zweites Mal wegspringen.
 */
let liegengeblieben: string | null = null

/**
 * Aus der Adresse einen Pfad für den Router machen — oder `null`.
 *
 * **Fremde Herkunft wird nicht gefolgt.** Die Nachricht kommt zwar vom eigenen
 * Service Worker, aber ein Sprung ist ein Sprung: was nicht von hier ist, hat
 * hier nichts zu suchen.
 *
 * **Und der Präfix muss weg.** `main.tsx` baut den `BrowserRouter` ohne
 * `basename`, während der Server mit `Config::basePath()` präfixt. Ungekürzt
 * landete `/travel/t/7` als unbekannter Pfad bei `<Route path="*">` und damit
 * auf der Startseite.
 */
export function alsPfad(url: string): string | null {
  try {
    const ziel = new URL(url, location.href)
    if (ziel.origin !== location.origin) return null
    let pfad = ziel.pathname
    if (BASE !== '' && pfad.startsWith(BASE)) pfad = pfad.slice(BASE.length)
    return (pfad === '' ? '/' : pfad) + ziel.search
  } catch {
    return null
  }
}

/**
 * Zuhören — **vor dem ersten Zeichnen**, in `main.tsx`.
 *
 * **`startMessages()` ist die Zeile, die man vergisst.** Wer wie hier
 * `addEventListener` statt `onmessage` benutzt, bekommt Nachrichten erst nach
 * diesem Aufruf; bis dahin stapeln sie sich stumm. Damit ist auch die
 * Reihenfolge von Registrierung und Übernahme des Controllers gleichgültig:
 * `client.postMessage()` geht an das Fenster, nicht durch den Controller.
 */
export function beobachteTippziel(): void {
  if (!('serviceWorker' in navigator)) return

  navigator.serviceWorker.addEventListener('message', (ereignis) => {
    const daten = ereignis.data as { typ?: string; url?: string } | null
    if (daten?.typ !== 'tb-ziel' || typeof daten.url !== 'string') return

    const pfad = alsPfad(daten.url)
    if (pfad === null) return

    // **Erst antworten, dann springen.** Der Service Worker wartet eine halbe
    // Sekunde auf ein Lebenszeichen; bleibt es aus, nimmt er den alten Weg.
    ereignis.ports[0]?.postMessage({ ok: true })

    if (empfaenger === null) liegengeblieben = pfad
    else empfaenger(pfad)
  })
  navigator.serviceWorker.startMessages()
}

/** Der Router meldet sich an. Was liegengeblieben ist, kommt sofort. */
export function abonniereTippziel(wer: Empfaenger): () => void {
  empfaenger = wer
  if (liegengeblieben !== null) {
    const pfad = liegengeblieben
    liegengeblieben = null
    wer(pfad)
  }
  return () => {
    if (empfaenger === wer) empfaenger = null
  }
}
